import { useCallback, useEffect, useState } from "react";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";

import { workspacesApi } from "@dashboard/workspaces/api";
import { showErrorToast } from "@shared/toasts/Toasts";

import { createClient, CreateClientPayload, fetchClients, FetchClientsParams, FetchClientsResponse } from "../api";

interface UseClientsState {
    data: FetchClientsResponse | null;
    loading: boolean;
}

export const useClients = (params: FetchClientsParams) => {
    const dispatch = useDispatch();
    const [state, setState] = useState<UseClientsState>({
        data: null,
        loading: true,
    });

    const fetchData = async () => {
        setState((prev) => ({ ...prev, loading: true }));
        const data = await fetchClients(params);
        setState({ data, loading: false });
    };

    const createClientAndUpdate = useCallback(
        async (payload: CreateClientPayload) => {
            const newClient = await createClient(payload);
            dispatch(workspacesApi.util.invalidateTags(["Workspaces"]));

            setState((prev) => {
                if (!prev.data) {
                    return prev;
                }

                return {
                    ...prev,
                    data: {
                        ...prev.data,
                        count: prev.data.count + 1,
                        list: [newClient, ...prev.data.list],
                    },
                };
            });
            return newClient;
        },
        [dispatch],
    );

    useEffect(() => {
        const loadData = async () => {
            try {
                await fetchData();
            } catch (error) {
                setState((prev) => ({
                    ...prev,
                    loading: false,
                }));
                const err = error as Error;
                showErrorToast(err.message || i18next.t("pages:clientsOverviewPage.toast.fetchClientsError"));
            }
        };

        loadData();
    }, [params.workspaceId, params.pageIndex, params.pageSize, params.sortCriteria, params.sortDirection]);

    const refetch = useCallback(async () => {
        try {
            await fetchData();
        } catch (error) {
            setState((prev) => ({
                ...prev,
                loading: false,
            }));
            const err = error as Error;
            showErrorToast(err.message || i18next.t("pages:clientsOverviewPage.toast.fetchClientsError"));
        }
    }, []);

    return {
        ...state,
        refetch,
        createClientAndUpdate,
    };
};
